import React from 'react';
import { Capacitor } from '@capacitor/core';
import { TextZoom } from '@capacitor/text-zoom';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { lazy, Suspense, useEffect } from 'react';
import { StatusBar, Style } from '@capacitor/status-bar';
import { useVersionCheck } from './hooks/useVersionCheck';
import WebUpdateBanner from './components/WebUpdateBanner';
import UpgradePopupHost from './components/UpgradePopup';
import UpdateScreen from './components/UpdateScreen';
import PromotionPopup from './components/PromotionPopup';

// Lazy load tất cả các trang
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RecipesPage = lazy(() => import('./pages/RecipesPage'));
const RecipeDetailPage = lazy(() => import('./pages/RecipeDetailPage'));
const MyRecipesPage = lazy(() => import('./pages/MyRecipesPage'));
const MenuPlanner = lazy(() => import('./pages/MenuPlanner'));
const AdminImport = lazy(() => import('./pages/AdminImport'));
const PricesPage = lazy(() => import('./pages/PricesPage'));
const CommunityPricesPage = lazy(() => import('./pages/CommunityPricesPage'));
const SavedMenus = lazy(() => import('./pages/SavedMenus'));
const SavedMenuDetail = lazy(() => import('./pages/SavedMenuDetail'));
const ShoppingList = lazy(() => import('./pages/ShoppingList'));
const MorePage = lazy(() => import('./pages/MorePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const FirstTimeSetupPage = lazy(() => import('./pages/FirstTimeSetupPage'));
const WaitingApprovalPage = lazy(() => import('./pages/WaitingApprovalPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminActivityPage = lazy(() => import('./pages/AdminActivityPage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const SubscriptionExpiredPage = lazy(() => import('./components/SubscriptionExpiredPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'));

// Loading spinner khi chuyển trang
const PageLoader = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: 40, height: 40, border: '3px solid #e5e7eb', borderTop: '3px solid #16a34a', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }}></div>
      <p style={{ marginTop: 12, color: '#6b7280', fontSize: 14 }}>Đang tải...</p>
    </div>
  </div>
);

const APP_VERSION = require('../package.json').version;

// Helper function to compare semantic versions
const compareVersions = (v1, v2) => {
  const parseVersion = (v) => {
    try {
      return v.split('.').map(x => parseInt(x) || 0);
    } catch {
      return [0, 0, 0];
    }
  };
  
  const [major1, minor1, patch1] = parseVersion(v1);
  const [major2, minor2, patch2] = parseVersion(v2);
  
  if (major1 !== major2) return major1 - major2;
  if (minor1 !== minor2) return minor1 - minor2;
  return patch1 - patch2;
};

function App() {
  // Lock font scale on Android
  React.useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      TextZoom.set({ value: 1.0 });
      // Force font lock via meta tag
      const meta = document.createElement('meta');
      meta.name = 'viewport';
      meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';
      document.head.appendChild(meta);
    }
  }, []);
  //useEffect(() => {
    // Setup status bar on mobile
    //const setupStatusBar = async () => {
    //  try {
    //    await StatusBar.setOverlaysWebView({ overlay: false });
    //    await StatusBar.setStyle({ style: Style.Dark });
    //  } catch (e) {
    //    console.log('StatusBar not available:', e);
    //  }
    //};
    
    //setupStatusBar();
  //}, []);

  // Version check
  const { versionStatus, isChecking, recheckVersion } = useVersionCheck();
  const [showSoftUpdate, setShowSoftUpdate] = React.useState(true);

  // Version check loading
  if (isChecking) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🛒</div>
          <div style={{ color: '#6b7280' }}>Đang khởi động AI Đi Chợ...</div>
        </div>
      </div>
    );
  }

  // Block app if force update or maintenance
  if (versionStatus && !versionStatus.can_run) {
    return <UpdateScreen versionStatus={versionStatus} recheckVersion={recheckVersion} />;
  }
  
  return (
    <AuthProvider>
      <Router>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route
              path="/admin/import"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminImport />
                </ProtectedRoute>
              }
            />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/prices" element={<PricesPage />} />
            <Route path="/community-prices" element={<CommunityPricesPage />} />          
            <Route
              path="/recipes"
              element={
                <ProtectedRoute>
                  <RecipesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/recipes/:id"
              element={
                <ProtectedRoute>
                  <RecipeDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-recipes"
              element={
                <ProtectedRoute>
                  <MyRecipesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/menu-planner"
              element={
                <ProtectedRoute>
                  <MenuPlanner />
                </ProtectedRoute>
              }
            />
            <Route
              path="/saved-menus"
              element={
                <ProtectedRoute>
                  <SavedMenus />
                </ProtectedRoute>
              }
            />
            <Route
              path="/saved-menus/:id"
              element={
                <ProtectedRoute>
                  <SavedMenuDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/saved-menus/:id/shopping-list"
              element={
                <ProtectedRoute>
                  <ShoppingList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/more"
              element={
                <ProtectedRoute>
                  <MorePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/subscription" 
              element={
                <ProtectedRoute>
                  <SubscriptionPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/help" 
              element={
                <ProtectedRoute>
                  <HelpPage />
                </ProtectedRoute>
              } 
            />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/delete-account" element={<DeleteAccountPage />} />
            <Route
              path="/first-time-setup"
              element={
                <ProtectedRoute>
                  <FirstTimeSetupPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/waiting-approval"
              element={
                <ProtectedRoute>
                  <WaitingApprovalPage />
                </ProtectedRoute>
              }
            />
            
            {/* ADD HERE - Subscription Expired Route */}
            <Route
              path="/subscription-expired"
              element={
                <ProtectedRoute>
                  <SubscriptionExpiredPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/activity"
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminActivityPage />
                </ProtectedRoute>
              }
            />
            
            <Route path="/" element={<Navigate to="/recipes" replace />} />
          </Routes>
        </Suspense>
        <UpgradePopupHost />
        {/* Soft update banner */}
        {versionStatus?.update_mode === 'soft' && 
        showSoftUpdate && 
        versionStatus?.server_version && 
        compareVersions(APP_VERSION, versionStatus.server_version) < 0 && (
          <UpdateScreen
            versionStatus={versionStatus}
            onSkip={() => setShowSoftUpdate(false)}
            recheckVersion={recheckVersion}
          />
        )}
      </Router>
      <PromotionPopup />
      <WebUpdateBanner />
    </AuthProvider>
  );
}

export default App;
